import { syncCaptures as apiSyncCaptures, ApiError } from './apiService';
import { buildCaptureAnswers } from '../utils/surveyForm';

const QUEUE_KEY = 'pulso17.captures.queue';
export const MAX_QUEUED_CAPTURES = 10;

/** Error específico para cuando la cola local ya llegó al máximo permitido. */
export class CaptureQueueFullError extends Error {
  constructor() {
    super(
      `Ya tienes ${MAX_QUEUED_CAPTURES} encuestas guardadas sin sincronizar. Sincroniza antes de capturar otra.`
    );
    this.name = 'CaptureQueueFullError';
  }
}

function generateUuid() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  // Fallback simple tipo uuid v4 para navegadores sin `crypto.randomUUID`.
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

/** Fecha-hora local en formato `YYYY-MM-DD HH:mm:ss`, como la espera el backend. */
function formatForApi(date) {
  const pad = (n) => String(n).padStart(2, '0');
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ` +
    `${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
  );
}

/**
 * Intenta obtener coordenadas GPS; si no hay permiso/soporte, resuelve
 * `{0, 0}` sin fallar.
 *
 * TODO: geolocalización deshabilitada por ahora — se retoma más
 * adelante. Mientras tanto, toda captura se envía con `latitude` y
 * `longitude` en `0` por defecto.
 */
function getCurrentCoords() {
  return Promise.resolve({ latitude: 0, longitude: 0 });

  /*
  return new Promise((resolve) => {
    if (!navigator.geolocation) {
      resolve({ latitude: 0, longitude: 0 });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (pos) => resolve({ latitude: pos.coords.latitude, longitude: pos.coords.longitude }),
      () => resolve({ latitude: 0, longitude: 0 }),
      { timeout: 4000, maximumAge: 60000 }
    );
  });
  */
}

function readQueue() {
  try {
    const raw = localStorage.getItem(QUEUE_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function writeQueue(queue) {
  localStorage.setItem(QUEUE_KEY, JSON.stringify(queue));
}

/** Capturas guardadas localmente, pendientes de sincronizar. */
export function getQueuedCaptures() {
  return readQueue();
}

/** Cantidad de capturas pendientes (para badges/KPIs). */
export function getQueuedCount() {
  return readQueue().length;
}

/**
 * Arma el objeto `capture` (formato `POST /captures/sync`) a partir de
 * una encuesta respondida y sus datos de contacto, e intenta obtener
 * la ubicación GPS del dispositivo.
 *
 * @param {{
 *   survey: import('./surveyService').Survey & { questions: Array },
 *   questionsByOrder: Map,
 *   answers: Object,
 *   sectionId: string|number|null,
 *   citizenName: string,
 *   address: string,
 * }} params
 */
export async function buildCapturePayload({
  survey,
  questionsByOrder,
  answers,
  sectionId,
  citizenName,
  address,
}) {
  const { latitude, longitude } = await getCurrentCoords();

  return {
    uuid: generateUuid(),
    survey_id: Number(survey.id),
    section_id: sectionId != null ? Number(sectionId) : null,
    citizen_name: citizenName.trim(),
    address: address.trim(),
    latitude,
    longitude,
    captured_at: formatForApi(new Date()),
    answers: buildCaptureAnswers(survey.questions, answers, questionsByOrder),
  };
}

/**
 * Guarda una captura en la cola local (localStorage). Lanza
 * `CaptureQueueFullError` si ya se alcanzó `MAX_QUEUED_CAPTURES` — hay
 * que sincronizar antes de seguir capturando.
 *
 * @param {Object} capture ver `buildCapturePayload`.
 */
export function enqueueCapture(capture) {
  const queue = readQueue();
  if (queue.length >= MAX_QUEUED_CAPTURES) {
    throw new CaptureQueueFullError();
  }
  writeQueue([...queue, capture]);
  return capture;
}

/**
 * Sincroniza todas las capturas pendientes contra `POST
 * /captures/sync`. Si la llamada tiene éxito, vacía la cola local por
 * completo (el backend recibió el lote entero); si falla, la cola
 * queda intacta para reintentar más tarde.
 *
 * @returns {Promise<{ synced: number }>}
 */
export async function syncQueuedCaptures() {
  const queue = readQueue();
  if (queue.length === 0) return { synced: 0 };

  await apiSyncCaptures(queue);
  writeQueue([]);
  return { synced: queue.length };
}

/** `true` si el error viene de que el backend no está disponible (útil para reintentos silenciosos). */
export function isNetworkError(err) {
  return err instanceof ApiError && err.status === 0;
}
